import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import { MainContext } from '@blaze-cms/nextjs-components';
import CharterRateColumn from './CharterRateColumn';

const getQuery = isPreview => gql`
  query getYachtForCharter($id: String!) {
    charterData: get${isPreview ? '' : 'Published'}YachtForCharter(id: $id) {
      id
      formattedWeeklyLowRate
      formattedWeeklyHighRate
      formattedSummerWeeklyLowRate
      formattedSummerWeeklyHighRate
      formattedWinterWeeklyLowRate
      formattedWinterWeeklyHighRate
    }
  }
`;

const CharterRateSummary = ({ parent, modifier }) => {
  const { isPreview } = useContext(MainContext);
  const { itemId } = parent;
  const { data = { charterData: {} }, loading } = useQuery(getQuery(isPreview), {
    variables: { id: itemId }
  });
  if (loading) return null;

  const {
    charterData: {
      formattedWeeklyLowRate,
      formattedWeeklyHighRate,
      formattedSummerWeeklyLowRate,
      formattedSummerWeeklyHighRate,
      formattedWinterWeeklyLowRate,
      formattedWinterWeeklyHighRate
    }
  } = data;

  const noRates = ![
    formattedSummerWeeklyLowRate,
    formattedSummerWeeklyHighRate,
    formattedWinterWeeklyLowRate,
    formattedWinterWeeklyHighRate,
    formattedWeeklyLowRate,
    formattedWeeklyHighRate
  ].filter(Boolean).length;
  const lowValues = [formattedSummerWeeklyLowRate, formattedWinterWeeklyLowRate].filter(Boolean);
  const lowValuesAllEqual = new Set(lowValues).size <= 1;
  const highValues = [formattedSummerWeeklyHighRate, formattedWinterWeeklyHighRate].filter(Boolean);
  const highValuesAllEqual = new Set(highValues).size <= 1;

  const noSummerValues = !formattedSummerWeeklyLowRate && !formattedSummerWeeklyHighRate;
  const noWinterValues = !formattedWinterWeeklyLowRate && !formattedWinterWeeklyHighRate;

  const missingSeasonValues = noSummerValues || noWinterValues;

  const calculatIsDisplayAllYear = () => {
    if (lowValuesAllEqual && highValuesAllEqual) {
      if (!noSummerValues && !noWinterValues) return true;
      if (noSummerValues && !noWinterValues) return false;
      if (!noSummerValues && noWinterValues) return false;

      return true;
    }

    return false;
  };

  // display all year if all values are equal or if all summer values are missing or all winter values are missing
  const displayAllYear = calculatIsDisplayAllYear();

  // if there are missing summer or winter values if all are set otherwise use all year values
  const allYearValueLowRate = !missingSeasonValues
    ? formattedSummerWeeklyLowRate || formattedWinterWeeklyLowRate
    : formattedWeeklyLowRate;
  const allYearHighRate = !missingSeasonValues
    ? formattedSummerWeeklyHighRate || formattedWinterWeeklyHighRate
    : formattedWeeklyHighRate;

  return (
    <div className={`charter-rate-summary ${modifier}`}>
      <h3>Seasonal charter rates</h3>
      <div className="charter-rate-summary--columns">
        {noRates && <CharterRateColumn title="" lowRate="Please enquire" isAllYear />}
        {!noRates && displayAllYear && (
          <CharterRateColumn
            title="All year"
            lowRate={allYearValueLowRate}
            highRate={allYearHighRate}
            isAllYear
          />
        )}
        {!noRates && !displayAllYear && (
          <>
            <CharterRateColumn
              title="Summer"
              lowRate={formattedSummerWeeklyLowRate}
              highRate={formattedSummerWeeklyHighRate}
            />
            <CharterRateColumn
              title="Winter"
              lowRate={formattedWinterWeeklyLowRate}
              highRate={formattedWinterWeeklyHighRate}
            />
          </>
        )}
      </div>
    </div>
  );
};

CharterRateSummary.propTypes = {
  modifier: PropTypes.string,
  parent: PropTypes.object.isRequired
};

CharterRateSummary.defaultProps = {
  modifier: ''
};

export default CharterRateSummary;
